import { TreeNodeNameDTO } from 'business/models/Models';
import { TreeService } from 'business/services/TreeService';
import React, { useContext, useEffect, useState } from 'react';
import { getParentPath, isNullOrUndefined } from 'utils/Global';
import './optimization.scss';
import {
  CheckBoxRow,
  OptimizationText,
  OptTextVariants,
  Table,
} from './OptimizationComponents';
import { OptimizationContext } from './OptimizationContext';
import {
  getLatestImageBatchId,
  hasPrecipetantIngredient,
  validateAllWellScores,
} from './IterativeScreenOptimizationHelper';
import useRockFetchGet from 'utils/RockFetch';
import ETreeNodeTypes from 'enums/ETreeNodeTypes';
import { useRMSelector } from 'redux/hooks';
import ETreeNodeAccessRightNames from 'enums/ETreeNodeAccessRightNames';
import { cloneDeep } from 'lodash';

export const ISOValuesTableView = () => {
  const [allWellsScored, setAllWellsScored] = useState<boolean>(true);
  const [warningAccept, setWarningAccept] = useState<boolean>(false);
  const [ingError, setIngError] = useState<boolean>(false);
  const [notImagedError, setNotImagedError] = useState<boolean>(false);
  const [accessRightError, setAccessRightError] = useState<boolean>(false);
  const [rockFetchGet] = useRockFetchGet();
  const context = useContext(OptimizationContext);
  const exp = useRMSelector((state) => state.experiment.expData);

  useEffect(() => {
    if (context.plateTypeId !== exp.containerDrops[0]?.containerId) {
      context.setPlateTypeId(exp.containerDrops[0]?.containerId);
    }
  });

  useEffect(() => {
    async function setNewExpUniqueName() {
      const treeService = new TreeService();
      const parentNodes = await treeService.getDocumentParentNodes(
        exp.expId || exp.experimentId,
        ETreeNodeTypes.Experiment,
      );
      if (parentNodes?.length === 0) {
        return;
      }
      const accessRight = await treeService.getTreeNodeAccessRightName(parentNodes[0].id);
      if (accessRight?.name === ETreeNodeAccessRightNames.Modify) {
        let url = `/api/Tree/getnewname/${parentNodes[0].id}/Experiment`;
        url += '?baseName=' + encodeURIComponent('' + getNewISOExpName());
        const expNode: TreeNodeNameDTO = await rockFetchGet(url, 'get', undefined, true);
        context.setExperimentName(expNode.name);
        context.setExperimentId(parentNodes[0].id);
        const expUrl = getParentPath(parentNodes);
        context.setExperimentUrl(expUrl);
        context.setIsNewExperiment(true);
        context.setOpenExperiment(true);
        if (!getLatestImageBatchId(exp)) {
          setNotImagedError(true);
          context.setNextEnabled(false);
        } else if (!hasPrecipetantIngredient(exp)) {
          setIngError(true);
        } else {
          const isScoredValid: boolean = await validateAllWellScores(
            exp,
            context.isoScores,
          );
          setAllWellsScored(isScoredValid);
          context.setNextEnabled(isScoredValid);
        }
      } else {
        setAccessRightError(true);
        context.setNextEnabled(false);
      }
    }
    setNewExpUniqueName();
  }, []);

  function onWarningCbChange() {
    if (!accessRightError) {
      context.setNextEnabled(!warningAccept);
    }
    setWarningAccept(!warningAccept);
  }

  function onCellChange(e, val) {
    const isoScores = cloneDeep(context.isoScores);
    for (const i in isoScores) {
      if (isoScores[i].scoreName == val) {
        if (e.target.value == '-') {
          isoScores[i].isoValue = e.target.value;
        } else {
          isoScores[i].isoValue = isNaN(parseInt(e.target.value))
            ? 0
            : parseInt(e.target.value);
        }
      }
    }
    context.setIsoScores([...isoScores]);
  }

  function getNewISOExpName(): string {
    let roundNumber: number = exp.plateDto.find((p) => p.plateNumber == 1).isoRoundNumber;
    roundNumber = isNullOrUndefined(roundNumber) ? 1 : roundNumber + 1;
    const iterationToken = '_ISO';

    let newExperimentName = exp.expName;
    if (newExperimentName.includes(iterationToken)) {
      const iterationIndex: number = newExperimentName.indexOf(iterationToken);
      const expName: string = newExperimentName.substring(0, iterationIndex);
      newExperimentName = expName + iterationToken + roundNumber;
    } else {
      newExperimentName = exp.expName + iterationToken + roundNumber;
    }
    return newExperimentName;
  }

  return (
    <div
      id="platerviewer-exp-isovaluetableview-wrapper"
      className="h-100 flex"
      style={{
        gap: '1rem',
        padding: '1rem',
        marginLeft: '5rem',
        marginRight: '5rem',
      }}
    >
      <div id="platerviewer-exp-isovaluetableview-leftpanel"
         className="p-2 h-100" style={{ flex: '50%', border: '1px dashed black' }}>
        <OptimizationText
          text={'Customize ISO% Values'}
          variant={OptTextVariants.heading}
        />
        <Table id="platerviewer-exp-isovaluetableview-table"
          columns={[
            { Header: 'Score Name', accessor: 'name', width: '80%' },
            { Header: 'ISO%', accessor: 'value', width: '20%' },
          ]}
          data={context.isoScores.map((val) => {
            return {
              name: val.scoreName,
              value: (
                <input
                  id={`platerviewer-exp-isovaluetableview-table-input-${val.scoreName}`}
                  type="text"
                  className="table-input"
                  onChange={(e) => onCellChange(e, val.scoreName)}
                  value={val.isoValue}
                />
              ),
            };
          })}
        />
      </div>
      <div
        id="platerviewer-exp-isovaluetableview-rightpanel"
        style={{
          flex: '50%',
          maxWidth: '50%',
          display: 'flex',
          flexDirection: 'column',
          flexWrap: 'wrap',
          gap: '1rem',
        }}
      >
        <div
          id="platerviewer-exp-isovaluetableview-info-box"
          className="p-2"
          style={{ display: 'flex', flexDirection: 'column', border: '1px dashed black' }}
        >
          <OptimizationText
            text={'Iterative Screen Optimization'}
            variant={OptTextVariants.heading}
          />
          <OptimizationText
            text={`You are about to optimizing ${exp.expName} using default score group as step reference. The new experiment will be saved in the same project folder with the mother plate.`}
          />
          <div id="platerviewer-exp-isovaluetableview-experimentname-row" style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'baseline' }}>
            <label id="platerviewer-exp-isovaluetableview-experimentname-label" style={{ fontWeight: 400, flex: '40%', fontSize: '13px' }}>
              Experiment Name
            </label>
            <input
              id="platerviewer-exp-isovaluetableview-experimentname-input"
              type="text"
              style={{ height: '2rem', flex: '60%' }}
              onChange={(e) => {
                context.setExperimentName(e.target.value);
              }}
              value={context.experimentName}
              readOnly
            />
          </div>
        </div>

        {!allWellsScored && (
          <div id="platerviewer-exp-isovaluetableview-warn-notscored-box"
              className="p-2" style={{ border: '1px dashed black' }}>
            <OptimizationText
              text={'Warning Message'}
              variant={OptTextVariants.warning}
            />
            <OptimizationText
              text={
                'Not all the drops in your experiment are scored. If you click "Yes", those unscored drops will be treated as "Clear". Alternatively, you may click "CANCEL" to restart the process to assign scores manually.'
              }
            />
            <CheckBoxRow
              isChecked={warningAccept}
              onChange={onWarningCbChange}
              label="Yes, Unscored drops will be treated as “Clear”"
            />
          </div>
        )}
        {ingError && (
          <div id="platerviewer-exp-isovaluetableview-error-ingredient-box" className="p-2" style={{ border: '1px dashed black' }}>
            <OptimizationText
              text={'Error Message'}
              variant={OptTextVariants.error}
              style={{ fontWeight: '700' }}
            />
            <br />
            <OptimizationText
              text="No Custom/Screen layer with precipitant ingredient found in the plate."
              variant={OptTextVariants.error}
            />
            <br />
            <OptimizationText text="Tip : Add a Custom/Screen Layer with precipitant ingredient and save before using Iterative Screen Optimization" />
          </div>
        )}
        {notImagedError && (
          <div id="platerviewer-exp-isovaluetableview-error-notimaged-box" 
            className="p-2" style={{ border: '1px dashed black' }}>
            <OptimizationText
              text={'Error Message'}
              variant={OptTextVariants.error}
              style={{ fontWeight: '700' }}
            />
            <br />
            <OptimizationText
              text="This experiment is not imaged."
              variant={OptTextVariants.error}
            />
            <br />
            <OptimizationText text="Tip : Experiment should be imaged/scored before using Iterative Screen Optimization" />
          </div>
        )}
        {accessRightError && (
          <div id="platerviewer-exp-isovaluetableview-error-accesright-box"
            className="p-2" style={{ border: '1px dashed black' }}>
            <OptimizationText
              text={'Error Message'}
              variant={OptTextVariants.error}
              style={{ fontWeight: '700' }}
            />
            <br />
            <OptimizationText
              text="You need Modify access to perform this operation"
              variant={OptTextVariants.error}
            />
          </div>
        )}
      </div>
    </div>
  );
};
